<template>
  <section class="signup-step__card signup-card signup-fade-up">
    <slot name="header">
      <div class="signup-step__header signup-stagger">
        <div v-if="eyebrow" class="signup-eyebrow">{{ eyebrow }}</div>
        <h1
          class="signup-display"
          v-html="title"
        ></h1>
        <p v-if="subtitle" class="signup-subtitle">{{ subtitle }}</p>
      </div>
    </slot>

    <div class="signup-step__body">
      <slot />
    </div>

    <slot name="footer">
      <footer class="signup-step__footer">
        <button
          type="button"
          class="signup-btn signup-btn--ghost"
          :disabled="loading"
          @click="onBack"
        >
          {{ backLabel }}
        </button>
        <button
          type="button"
          class="signup-btn signup-btn--primary"
          :disabled="!canGoNext || loading"
          @click="onNext"
        >
          <span v-if="loading" class="signup-step__spinner" aria-hidden="true"></span>
          <span>{{ resolvedNextLabel }}</span>
        </button>
      </footer>
    </slot>
  </section>
</template>

<script>
/**
 * SignupStepFrame — the per-step CARD only. Phase F refactor: the
 * persistent atmospheric shell (background gradient, CloudLayer, topbar
 * with BrandMark + progress + Save&exit) lives in SignupWizard.vue, so
 * stepping between Step 1 → 2 only swaps the card content. This avoids
 * the white flash that came from re-mounting the entire shell on every
 * route change.
 *
 * Contract (used by step components 1..6):
 *  Props:
 *   - eyebrow:    String      — tiny-caps label above the title (e.g. "Step 1 — Company")
 *   - title:      String      — Instrument Serif italic headline. May contain <b>…</b>
 *                                for an inline Lato-black emphasis (rendered via v-html).
 *                                IMPORTANT: pass STATIC template strings only — this is
 *                                not sanitized. User input must NOT be interpolated here.
 *   - subtitle:   String      — supporting copy under the title
 *   - nextLabel:  String      — default "Continue"; if `isLastStep` is true, overridden
 *                                to "Create account & invite team" unless nextLabel is
 *                                explicitly set by the caller.
 *   - backLabel:  String      — default "Back"
 *   - canGoNext:  Boolean     — disables the primary CTA when false (default true)
 *   - loading:    Boolean     — shows an inline spinner in the primary CTA and disables
 *                                both buttons. Use on step 6 during submitSignup.
 *   - isLastStep: Boolean     — default false; swaps the CTA label when nextLabel omitted.
 *   - stepNumber, stepTotal:  — kept for back-compat (no-ops here; the wizard's persistent
 *                                topbar drives progress display).
 *
 *  Slots:
 *   - default         — step body content (form fields, etc.)
 *   - header (opt.)   — override the eyebrow+title+subtitle block entirely
 *   - footer (opt.)   — override Back/Continue (e.g. for a review page needing a
 *                       single "Create account & invite team" button)
 *
 *  Emits:
 *   - next        — Continue clicked (caller should route forward or submit)
 *   - back        — Back clicked (caller should route backward)
 *   - saveAndExit — bubbled from the wizard's topbar exit button via $emit (no-op here)
 */
export default {
  name: 'SignupStepFrame',
  props: {
    stepNumber: { type: Number, default: 1 },
    stepTotal:  { type: Number, default: 6 },
    eyebrow:    { type: String, default: '' },
    title:      { type: String, default: '' },
    subtitle:   { type: String, default: '' },
    nextLabel:  { type: String, default: '' },
    backLabel:  { type: String, default: 'Back' },
    canGoNext:  { type: Boolean, default: true },
    loading:    { type: Boolean, default: false },
    isLastStep: { type: Boolean, default: false },
  },
  computed: {
    resolvedNextLabel() {
      if (this.nextLabel) return this.nextLabel
      if (this.isLastStep) return 'Create account & invite team'
      return 'Continue'
    },
  },
  methods: {
    onBack() {
      if (this.loading) return
      this.$emit('back')
    },
    onNext() {
      if (this.loading || !this.canGoNext) return
      this.$emit('next')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

/* Phase G — card is a flex column with its body as the scroll region.
   This keeps the footer (Back / Continue) pinned to the card's bottom
   edge regardless of step content length, so the user never scrolls
   to find the navigation. */
.signup-step__card {
  width: 100%;
  max-width: 720px;
  max-height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) { border-radius: 14px; }

  /* Direct children (default header + body + default footer, OR custom
     header/footer slot content) all hold their intrinsic height; only
     the body grows + scrolls. */
  > * { flex-shrink: 0; }
}

.signup-step__header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 28px 36px 0;
  margin-bottom: 18px;

  @media (max-width: 640px) {
    padding: 22px 22px 0;
    margin-bottom: 14px;
  }
}

.signup-step__card > .signup-step__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 36px 16px;

  @media (max-width: 640px) { padding: 0 22px 14px; }

  /* Subtle scrollbar so the chrome doesn't fight the wizard aesthetic.
     Visible only when content overflows. */
  scrollbar-width: thin;
  scrollbar-color: rgba(108, 48, 204, 0.22) transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(108, 48, 204, 0.18);
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb:hover { background: rgba(108, 48, 204, 0.32); }
}

.signup-step__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 36px 22px;
  border-top: 1px solid var(--signup-border-soft);
  /* Soft upward shadow so users with overflowing content see the
     footer "covers" content scrolling beneath it. Subtle enough to
     feel like a single surface when content fits. */
  box-shadow: 0 -10px 18px -14px rgba(30, 10, 60, 0.10);
  background: var(--signup-surface);

  @media (max-width: 640px) {
    padding: 14px 22px 18px;
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 12px;
    .signup-btn { width: 100%; }
  }
}

.signup-step__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  animation: signup-spin 720ms linear infinite;
}

@keyframes signup-spin {
  to { transform: rotate(360deg); }
}
</style>
